import { Button, Card, EmptyState } from '@bixo/ui'
import { Link } from '@tanstack/react-router'
import { Building2, Compass, Lock, PauseCircle } from 'lucide-react'
import { motion } from 'motion/react'
import type { ReactNode } from 'react'

export function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center bg-background" role="status" aria-label="Loading">
      <motion.div
        className="size-10 rounded-xl bg-linear-to-br from-brand-400 to-brand-700 shadow-glow"
        animate={{ scale: [1, 1.08, 1], rotate: [0, 6, 0] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  )
}

function FullPage({ children }: { children: ReactNode }) {
  return (
    <div className="bg-grid relative grid min-h-dvh place-items-center bg-background px-4">
      <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_50%_40%,transparent,var(--color-background))]" />
      <Card className="relative w-full max-w-md">{children}</Card>
    </div>
  )
}

export function WorkspaceNotFound() {
  return (
    <FullPage>
      <EmptyState
        icon={Building2}
        title="Workspace not found"
        description="There is no business at this address. Check the link you were given, or ask your administrator for the right one."
      />
    </FullPage>
  )
}

export function WorkspaceSuspended({ name }: { name?: string }) {
  return (
    <FullPage>
      <EmptyState
        icon={PauseCircle}
        title={`${name ?? 'This workspace'} is paused`}
        description="Access has been temporarily suspended. Your data is safe. Please contact your account owner or support to restore access."
      />
    </FullPage>
  )
}

export function WorkspaceUnavailable({ onRetry }: { onRetry: () => void }) {
  return (
    <FullPage>
      <EmptyState
        icon={Compass}
        title="We couldn't reach the server"
        description="Check your connection and try again."
        action={<Button onClick={onRetry}>Try again</Button>}
      />
    </FullPage>
  )
}

export function NotFound() {
  return (
    <EmptyState
      icon={Compass}
      title="Page not found"
      description="The page you're looking for doesn't exist or has moved."
      action={
        <Button asChild variant="secondary">
          <Link to="/">Back to dashboard</Link>
        </Button>
      }
    />
  )
}

export function Forbidden() {
  return (
    <Card>
      <EmptyState
        icon={Lock}
        title="You don't have access to this page"
        description="Ask your workspace owner to grant the permission if you need it."
        action={
          <Button asChild variant="secondary">
            <Link to="/">Back to dashboard</Link>
          </Button>
        }
      />
    </Card>
  )
}
